import React, { useState } from "react";
import { Bell, CheckCircle, Loader2 } from "lucide-react";

interface WaitlistSignupProps {
  variant?: "default" | "compact";
}

export const WaitlistSignup: React.FC<WaitlistSignupProps> = ({ variant = "default" }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMessage("Please enter a valid email address");
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch("/api/waitlist/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to sign up");
      }

      setStatus("success");
      setMessage("🎉 We added you to the waitlist! You'll be notified at launch.");
      setEmail("");
      
      // Launch confetti
      launchConfetti();
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    }
  };

  const launchConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const colors = ['#6366f1', '#a855f7', '#ec4899', '#06b6d4', '#10b981'];

    (function frame() {
      const confetti = document.createElement('div');
      confetti.style.position = 'fixed';
      confetti.style.width = '10px';
      confetti.style.height = '10px';
      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.borderRadius = Math.random() > 0.5 ? '50%' : '0';
      confetti.style.left = (Math.random() * 100) + '%';
      confetti.style.top = '-20px';
      confetti.style.zIndex = '9999';
      confetti.style.pointerEvents = 'none';
      document.body.appendChild(confetti);

      const fallDuration = Math.random() * 2000 + 1500;
      const startTime = Date.now();
      const startX = parseFloat(confetti.style.left);

      function animate() {
        const elapsed = Date.now() - startTime;
        const progress = elapsed / fallDuration;

        if (progress >= 1) {
          confetti.remove();
          return;
        }

        const y = progress * window.innerHeight;
        const x = startX + Math.sin(progress * Math.PI * 4) * 50;
        const rotation = progress * 720;
        
        confetti.style.transform = `translate(${x - startX}%, ${y}px) rotate(${rotation}deg)`;
        confetti.style.opacity = String(Math.max(0, 1 - progress));

        requestAnimationFrame(animate);
      }

      animate();

      if (Date.now() < animationEnd) {
        requestAnimationFrame(frame);
      }
    }());
  };

  if (variant === "compact") {
    return (
      <div className="w-full max-w-md">
        {status === "success" ? (
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm">{message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={status === "loading"}
              className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-all"
            >
              {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Notify Me"}
            </button>
          </form>
        )}
        {status === "error" && (
          <p className="mt-2 text-sm text-red-600">{message}</p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 md:p-12">
      <div className="max-w-2xl mx-auto text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-full mb-6">
          <Bell className="h-8 w-8 text-white" />
        </div>
        
        <h3 className="text-3xl font-bold text-gray-900 mb-4">
          Get Notified at Launch
        </h3>
        <p className="text-lg text-gray-600 mb-8">
          Be the first to know when ZillHub officially launches. Join our waitlist for exclusive early access and special launch pricing.
        </p>

        {status === "success" ? (
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-green-50 border-2 border-green-200 rounded-xl text-green-700">
            <CheckCircle className="h-6 w-6" />
            <p className="font-semibold">{message}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              disabled={status === "loading"}
              className="flex-1 px-6 py-4 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50 text-lg"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Joining...
                </>
              ) : (
                "Notify Me"
              )}
            </button>
          </form>
        )}
        
        {status === "error" && (
          <p className="mt-4 text-red-600 font-medium">{message}</p>
        )}
        
        {status !== "success" && (
          <p className="mt-4 text-sm text-gray-500">
            We'll only email you about the launch. No spam, ever.
          </p>
        )}
      </div>
    </div>
  );
};
